import { useState } from "react";

import { MoreHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LeadComponent,
  MainComponent,
  ParticipantComponent,
} from "@/types/programs";

type ProgramComponentType =
  | ParticipantComponent
  | MainComponent
  | LeadComponent;

interface ComponentContextButtonProps {
  component: ProgramComponentType;
  onEdit?: (component: ProgramComponentType) => void;
  onDelete?: (component: ProgramComponentType) => void;
  onClose?: (component: ProgramComponentType) => void;
  onGrantExtension?: (component: ProgramComponentType) => void;
  couldEndEarly: boolean;
  canGrantExtension: boolean;
  canEdit: boolean;
}

export function ComponentContextButton({
  component,
  onEdit,
  onDelete,
  onClose,
  onGrantExtension,
  couldEndEarly,
  canGrantExtension = false,
  canEdit = false,
}: ComponentContextButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const canEndEarly = canEdit && couldEndEarly;
  const canDelete = canEdit && !couldEndEarly;

  const editComponent = canEdit ? (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onEdit?.(component);
        setIsOpen(false);
      }}
    >
      Edit Component
    </DropdownMenuItem>
  ) : null;

  const endEarlyComponent = canEndEarly ? (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onClose?.(component);
        setIsOpen(false);
      }}
    >
      End Early
    </DropdownMenuItem>
  ) : null;

  const deleteComponent = canDelete ? (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onDelete?.(component);
        setIsOpen(false);
      }}
    >
      Delete Component
    </DropdownMenuItem>
  ) : null;

  const grantExtensionComponent = canGrantExtension ? (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onGrantExtension?.(component);
        setIsOpen(false);
      }}
    >
      Grant Extension
    </DropdownMenuItem>
  ) : null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          intent="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {editComponent}
        {grantExtensionComponent}
        {canEdit && <DropdownMenuSeparator />}
        {endEarlyComponent}
        {deleteComponent}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
